import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAppSelector } from '../store/hooks';
import { API_CONFIG } from '../config/api';
import { logger } from '../utils/logger';


export const useWebSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const { isAuthenticated } = useAppSelector((state: any) => state.auth);

  useEffect(() => {
    if (!isAuthenticated) return;

    const wsURL = API_CONFIG.getWebSocketURL();
    
    const socket = io(wsURL, {
      path: '/socket.io',
      withCredentials: true,
      // Polling primeiro costuma funcionar melhor atrás de proxy; depois upgrade para websocket
      transports: ['polling', 'websocket'],
      timeout: 30000,
      forceNew: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      logger.debug('🔌 WebSocket conectado');
    });

    socket.on('disconnect', (reason) => {
      if (reason !== 'io client disconnect') {
        logger.warn('🔌 WebSocket desconectado:', reason);
      }
    });

    socket.on('qr-code-scanned', () => {});
    socket.on('bot-disconnected', () => {});

    // Erros de conexão: log único em nível warning (evita poluir console)
    socket.on('connect_error', (error) => {
      const isTimeout = error.message === 'timeout';
      if (isTimeout) {
        logger.warn('🔌 WebSocket: conexão em tempo real indisponível (timeout). Mensagens continuam funcionando via API.');
      } else {
        logger.warn('🔌 WebSocket connect_error:', error.message);
      }
    });

    socket.on('error', (error) => {
      logger.warn('🔌 WebSocket error:', error instanceof Error ? error.message : error);
    });

    socket.on('reconnect_error', (error) => {
      logger.warn('🔌 WebSocket reconnect_error:', error.message);
    });

    socket.on('reconnect_failed', () => {
      logger.warn('🔌 WebSocket: reconexão esgotada. Atualize a página para tentar novamente.');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated]);

  const emit = (event: string, data: any) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data);
    }
  };

  const on = (event: string, callback: (...args: any[]) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  };

  const off = (event: string, callback: (...args: any[]) => void) => {
    if (socketRef.current) {
      socketRef.current.off(event, callback);
    }
  };

  const joinBotRoom = (botId: number) => {
    emit('join-bot-room', botId);
  };

  const leaveBotRoom = (botId: number) => {
    emit('leave-bot-room', botId);
  };

  return {
    socket: socketRef.current,
    emit,
    on,
    off,
    joinBotRoom,
    leaveBotRoom,
    isConnected: socketRef.current?.connected || false
  };
};